import React from 'react';

import { ICONS } from '@/constants/icons';

import styles from './Icon.module.scss';

/**
 * Icon — canonical FontAwesome glyph resolved from the semantic registry (ICONS).
 * Single entry point for iconography: pass a functional `name`, not a raw FA class.
 *
 * Props:
 * - name:  semantic key from `@/constants/icons` (es. "edit", "delete", "user")
 * - size:  sm | md | lg | xl → token --icon-size-*; a number sets px inline. Omit to
 *          inherit font-size from the context (like a bare <i>) — the non-regressive default
 * - tint:  color family (primary | teal | success | warning | danger | info | purple |
 *          neutral | …) → var(--color-<tint>); omit to inherit currentColor
 * - title: when present, the icon is exposed to AT (role="img" + aria-label); otherwise
 *          it is decorative (aria-hidden)
 */
export default function Icon({ name, size, tint, title, className = '', style, ...rest }) {
  const glyph = ICONS[name];
  if (import.meta.env.DEV && !glyph) {
    console.warn(`[Icon] unknown icon name: "${name}"`);
  }

  const sizeClass = typeof size === 'string' ? styles[size] : undefined;
  const inlineStyle = {
    ...(typeof size === 'number' ? { fontSize: size } : null),
    ...(tint ? { color: `var(--color-${tint})` } : null),
    ...style,
  };

  const a11y = title
    ? { role: 'img', 'aria-label': title, title }
    : { 'aria-hidden': 'true' };

  return (
    <i
      className={`${glyph || ''} ${sizeClass || ''} ${className}`.trim()}
      style={Object.keys(inlineStyle).length ? inlineStyle : undefined}
      {...a11y}
      {...rest}
    />
  );
}
